import { useMemo, useCallback } from 'react';
import StatusPopover from '../../../../components/v2/StatusPopover.jsx';
import { Link } from 'react-router-dom';
import StatusBadge from './StatusBadge.jsx';
import GroupBadge from './GroupBadge.jsx';
import DeviceCell from './DeviceCell.jsx';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import EmptyState from './EmptyState.jsx';
import { AREA_TYPES } from '../data/mockData.js';
import AlertBellPopover from '../../../../components/v2/AlertBellPopover.jsx';
import { hasEhrResidentId } from '../../../../services/session.js';
import { convertIsoToLocal } from '../../../../libs.jsx';
import { relativeTimeFromCampusLocal, utcToCampusDate } from '../../../../utils/campusTime.js';
import RowActionsMenu, { BlockableMenuItem } from '../../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

/**
 * ResidentGroupsCell — inline pill tags for each group name.
 */
const ResidentGroupsCell = ({ groups }) => {
  if (!groups || groups.length === 0) {
    return <span className="rp-cell-text--muted">&mdash;</span>;
  }

  return (
    <div className="rp-group-pills">
      {groups.map((g, i) => (
        <span key={g.resident_group_id || i} className="rp-group-pill">
          {g.resident_group_name || g}
        </span>
      ))}
    </div>
  );
};

/**
 * Column definitions for the residents table.
 * Each column has: key, label, sortable, filterable, required (can't hide), render fn.
 */
/* A moved-out resident (is_active falsey) is read-only on this page: View Profile is the
   only row action, and the bulk writes skip them. Move back in, delete and the rest
   live on ResidentProfilePage. Same truthiness test as the Status column. */
export const isMovedOut = (row) => !row?.is_active;

export const COLUMN_DEFS = [
  { key: 'resident_first_name', label: 'First Name', sortable: true, filterable: true, required: true },
  { key: 'resident_last_name', label: 'Last Name', sortable: true, filterable: true, required: true },
  { key: 'resident_nickname', label: 'Nickname', sortable: true, filterable: true, required: false },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true, required: true },
  { key: 'area_type_name', label: 'Area Type', sortable: true, filterable: true, required: false },
  // Gated column — only surfaced by ResidentsPage when an active RTLS vendor
  // (vendor_id 14) is configured. Location data is mapped onto the resident row
  // from the Inovonics locations fetch (by device serial).
  { key: 'locating', label: 'Real-time Locating', sortable: true, filterable: false, required: false },
  { key: 'resident_groups', label: 'Groups', sortable: false, filterable: true, required: false },
  { key: 'resident_phone', label: 'Phone', sortable: true, filterable: false, required: false },
  { key: 'resident_email', label: 'Email', sortable: true, filterable: false, required: false },
  { key: 'ehr_level_of_care', label: 'Care Level', sortable: true, filterable: true, required: false },
  /* Gated column — only surfaced by ResidentsPage when the campus has an active EHR
     integration. Not filterable: the values are per-resident partner ids, so the
     filter list would be one entry per resident. Sorting is the useful operation,
     since it groups the residents the partner has not synced yet. */
  { key: 'ehr_resident_id', label: 'EHR ID', sortable: true, filterable: false, required: false },
  { key: 'devices', label: 'Devices', sortable: false, filterable: true, required: false },
  { key: 'check_in_time', label: 'Last Check-in', sortable: true, filterable: false, required: false },
  /* Stored UTC, shown as the campus-local date. Not filterable, like Last Check-in:
     one value per resident would make the filter list as long as the table. Sorts on
     the raw "YYYY-MM-DD HH:MM:SS" string, which orders the same as the instant. */
  { key: 'move_in_date', label: 'Move-in Date', sortable: true, filterable: false, required: false },
  { key: 'status', label: 'Status', sortable: true, filterable: true, required: true },
];

/**
 * ResidentTable — full data table with sorting, filtering, sticky header,
 * row selection, row actions, and consistent row heights.
 */
const ResidentTable = ({
  data,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEditResident,
  /* (row) => boolean. True means this resident's data is owned elsewhere (EHR) or the
     user has no edit role, so every write action on the row is hidden. Per-row rather
     than per-page: on a receiving campus a portal-created resident stays editable
     while one the partner owns does not. */
  isRowLocked = () => false,
  /* (row) => string | null. Separate from isRowLocked because the two behave
     differently on purpose: a locked row HIDES its write actions (the EHR owns them
     and always will), while an alarming row SHOWS them greyed with the reason,
     because the block clears on its own in a minute. */
  getAlarmBlockedReason = () => null,
  /* (row) => string | null. Why Edit is unavailable. Unlike the other locked
     actions, Edit stays VISIBLE and disabled so the reason is discoverable rather
     than the control silently vanishing. */
  getEditLockedReason = () => null,
  /* Map<residentIdString, alert[]> from alertsByResident(). A Map rather than a
     filter callback so the table does one pass over the alerts, not one per row. */
  alertsByResidentId,
  tzOffset = 0,
  onManageDevices,
  onMoveOut,
  onRelocate,
  onAddResident,
  onClearFilters,
  allData,
}) => {
  const activeColumns = useMemo(
    () => COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  /* Ids compare as strings: resident_id arrives as a number on the row while the
     alert ids are normalized to strings. Declared above renderCell and the row map,
     both of which call it. */
  const alarmsFor = useCallback(
    (residentId) => alertsByResidentId?.get(String(residentId)) || null,
    [alertsByResidentId]
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = useCallback(
    (columnKey) => {
      const values = new Set();
      (allData || data).forEach((row) => {
        if (columnKey === 'resident_groups') {
          const groups = row.resident_groups || [];
          if (groups.length === 0) {
            values.add('(empty)');
          } else {
            groups.forEach((g) => values.add(g.resident_group_name));
          }
        } else if (columnKey === 'devices') {
          // Filter on device_name, matching what the Devices popover lists.
          const devs = row.devices || [];
          if (devs.length === 0) {
            values.add('(empty)');
          } else {
            devs.forEach((d) => { if (d.device_name) values.add(d.device_name); });
          }
        } else if (columnKey === 'status') {
          values.add(row.status || (row.is_active ? 'Active' : 'Moved Out'));
        } else {
          const val = row[columnKey];
          values.add(val != null && val !== '' ? String(val) : '(empty)');
        }
      });
      return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    [allData, data]
  );

  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.resident_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'resident_first_name': {
        /* The marker rides the first data column rather than getting one of its own:
           it is where the eye lands, and an extra column would cost width on a table
           that already scrolls sideways. The name keeps its own colour — it is a
           link, and its blue is the affordance that says so. */
        const alarms = alarmsFor(row.resident_id);
        return (
          <>
            {alarms ? (
              <AlertBellPopover alerts={alarms} tzOffset={tzOffset} scope="resident" />
            ) : null}
            <Link to={`/community/residents/${row.resident_id}`} className="rp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', textDecoration: 'none' }}>
              {row.resident_first_name || '—'}
            </Link>
            {alarms ? (
              <span className="pp-alarm-chip">
                <span className="pp-alarm-chip__dot" aria-hidden="true" />
                {alarms.length > 1 ? `${alarms.length} in alarm` : 'In alarm'}
              </span>
            ) : null}
          </>
        );
      }

      case 'resident_last_name':
        return (
          <Link to={`/community/residents/${row.resident_id}`} className="rp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', textDecoration: 'none' }}>
            {row.resident_last_name || '—'}
          </Link>
        );

      case 'ehr_resident_id': {
        /* Same emptiness rule the edit lock uses (hasEhrResidentId): null, '', '0'
           and whitespace all mean "the partner does not own this resident yet", so
           they render as a dash rather than a literal 0 that reads like an id. */
        const ehrId = hasEhrResidentId(row) ? String(row.ehr_resident_id).trim() : '';
        return ehrId
          ? <span className="rp-cell-text">{ehrId}</span>
          : <span className="rp-cell-text rp-cell-text--muted">{'—'}</span>;
      }

      case 'area_type_name':
        return (
          <span
            className="rp-area-type-tag"
            title={AREA_TYPES[row.area_type_name] || row.area_type_description || row.area_type_name}
          >
            {row.area_type_name}
          </span>
        );

      case 'resident_groups':
        return <ResidentGroupsCell groups={row.resident_groups || []} />;

      case 'devices':
        return (
          <DeviceCell
            devices={row.devices || []}
            onClick={isMovedOut(row) ? undefined : () => onManageDevices?.(row)}
          />
        );

      case 'check_in_time':
        return (
          /* check_in_time is campus-local wall clock: the popover shows it as stored, and
             "time ago" anchors it to the campus offset. Both parsed it with new Date(),
             which reads it in the viewer's timezone and fails on Safari. */
          <StatusPopover message={convertIsoToLocal(row.check_in_time)}>
            <span className={`rp-cell-text rp-cell-text--secondary ${row?.check_in_time ? "has-popover" : ""}`}>
              {relativeTimeFromCampusLocal(row.check_in_time, tzOffset)}
            </span>
          </StatusPopover>
        );

      case 'move_in_date': {
        const moveIn = utcToCampusDate(row.move_in_date, tzOffset);
        return moveIn
          ? <span className="rp-cell-text rp-cell-text--nowrap">{moveIn}</span>
          : <span className="rp-cell-text rp-cell-text--muted">{'—'}</span>;
      }

      case 'status': {
        const status = row.status || (row.is_active ? 'Active' : 'Moved Out');
        /* move_out_date is stored UTC, like move_in_date. Only a moved-out resident
           gets the hover, so a resident moved back in never shows a leftover date.
           No message (none recorded, or the zero date) renders the bare badge. */
        const movedOut = status === 'Moved Out' ? utcToCampusDate(row.move_out_date, tzOffset) : '';
        return (
          <StatusPopover message={movedOut ? `Moved out on ${movedOut}` : null}>
            <StatusBadge status={status} />
          </StatusPopover>
        );
      }

      case 'locating': {
        // RTLS location mapped onto the resident (by device serial) in
        // ResidentsPage. row.location = { area_name, area_id, timestamp }; the
        // area name links to its AreaProfilePage when an area_id is present, and
        // hovering shows the (campus-local) timestamp of the reading.
        const loc = row.location;
        if (!loc?.area_name) return <span className="rp-cell-text rp-cell-text--secondary">—</span>;
        const areaEl = loc.area_id ? (
          <Link to={`/community/areas/${loc.area_id}`} className="rp-cell-text rp-cell-text--link" style={{ textDecoration: 'none' }}>
            {loc.area_name}
          </Link>
        ) : (
          <span className="rp-cell-text">{loc.area_name}</span>
        );
        return loc.timestamp
          ? <StatusPopover message={`Last update: ${loc.timestamp}`}>{areaEl}</StatusPopover>
          : areaEl;
      }

      case 'resident_phone':
        return <span className="rp-cell-text rp-cell-text--nowrap">{row.resident_phone || '—'}</span>;

      case 'resident_email':
        return <span className="rp-cell-text rp-cell-text--truncate">{row.resident_email || '—'}</span>;

      case 'ehr_level_of_care':
        return <span className="rp-cell-text">{row.ehr_level_of_care || '—'}</span>;

      case 'area_name': {
        // Quick-relocate shortcut sits next to the area so a move can be
        // started without opening the kebab. Same icon as the kebab item so
        // the affordance is learnable. Only for active residents — a moved-out
        // resident has no room to move out of.
        const canRelocate = !isMovedOut(row) && typeof onRelocate === 'function' && !isRowLocked(row);
        const relocateBlocked = getAlarmBlockedReason(row);
        return (
          <span className="rp-area-cell">
            {row.area_id ? (
              <Link to={`/community/areas/${row.area_id}`} className="rp-cell-text rp-cell-text--link" style={{ textDecoration: 'none' }}>
                {row.area_name}
              </Link>
            ) : (
              <span className="rp-cell-text">{row.area_name || '—'}</span>
            )}
            {canRelocate ? (
              <button
                type="button"
                className={`rp-relocate-btn${relocateBlocked ? ' is-blocked' : ''}`}
                /* Anchor for the interactive help guide — see
                   docs/INTERACTIVE_HELP_PLAN.md §5.2. One match per row; the
                   guide spotlights the first viewport-visible one. */
                data-help-id="residents.row-relocate"
                /* Left clickable with aria-disabled rather than `disabled`, so the
                   title still surfaces — Chrome fires no pointer events on a
                   disabled control. ResidentsPage.handleOpenRelocate re-checks and
                   answers with a toast, so the click cannot get through. */
                aria-disabled={relocateBlocked ? true : undefined}
                onClick={(e) => { e.stopPropagation(); onRelocate(row); }}
                title={relocateBlocked || `Relocate ${row.resident_first_name || ''} ${row.resident_last_name || ''}`.trim()}
                aria-label={`Relocate ${row.resident_first_name || ''} ${row.resident_last_name || ''}`.trim()}
              >
                <RelocateIcon className="rp-icon-sm" />
              </button>
            ) : null}
          </span>
        );
      }

      default:
        return <span className="rp-cell-text">{row[columnKey] || '—'}</span>;
    }
  };

  if (data.length === 0) {
    const hasAnyData = (allData || []).length > 0;
    return (
      <EmptyState
        type={hasAnyData ? 'no-results' : 'no-data'}
        onAddResident={hasAnyData ? undefined : onAddResident}
        onClearFilters={hasAnyData ? onClearFilters : undefined}
      />
    );
  }

  return (
    <div className="rp-table-wrap">
      <table className="rp-table">
        <thead className="rp-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="rp-th rp-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onSelectAll}
                className="rp-checkbox"
                aria-label="Select all residents"
              />
            </th>

            {/* Actions column */}
            <th scope="col" className="rp-th rp-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="rp-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                <div className="rp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="rp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable && (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="rp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.resident_id);
            const rowAlarms = alarmsFor(row.resident_id);
            return (
              <tr
                key={row.resident_id}
                className={`rp-tr ${isSelected ? 'is-selected' : ''}${rowAlarms ? ' has-alarm' : ''}`}
              >
                {/* Checkbox */}
                <td className="rp-td rp-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.resident_id)}
                    className="rp-checkbox"
                    aria-label={`Select ${row.resident_first_name} ${row.resident_last_name}`}
                  />
                </td>

                {/* Row actions kebab */}
                <td className="rp-td rp-td--actions">
                  <ResidentRowActions
                    onRelocate={onRelocate}
                    resident={row}
                    onEdit={onEditResident}
                    onManageDevices={onManageDevices}
                    onMoveOut={onMoveOut}
                    locked={isRowLocked(row)}
                    alarmBlockedReason={getAlarmBlockedReason(row)}
                    editLockedReason={getEditLockedReason(row)}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="rp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * RelocateIcon — bidirectional transfer arrows, used for the Relocate action in
 * both the kebab menu and the Area column shortcut so the two read as the same
 * action.
 */
const RelocateIcon = ({ className = 'rp-icon-md rp-row-actions__item-icon' }) => (
  <svg style={{width: "1rem", height: "1rem"}} className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.75}
       stroke="currentColor" aria-hidden="true">
    {/* Arrow entering a doorway — "move into a new room". Note this is the
        mirror of the Move Out icon in this same kebab; the two are deliberately
        distinguished only by arrow direction, so keep their labels adjacent and
        clear. */}
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0110.5 3h6a2.25 2.25 0 012.25 2.25v13.5A2.25 2.25 0 0116.5 21h-6a2.25 2.25 0 01-2.25-2.25V15M12 9l3 3m0 0l-3 3m3-3H2.25" />
  </svg>
);

/**
 * ResidentRowActions — kebab menu for per-row actions (shell: components/v2/RowActionsMenu).
 */
const ResidentRowActions = ({ resident, onEdit, onManageDevices, onMoveOut, onRelocate, locked = false, alarmBlockedReason = null, editLockedReason = null }) => {
  /* Moved out: View Profile only (see isMovedOut). */
  const active = !isMovedOut(resident);
  return (
    <RowActionsMenu
      label={`Actions for ${resident.resident_first_name} ${resident.resident_last_name}`}
      classPrefix="rp"
      /* Help-guide anchor (docs/INTERACTIVE_HELP_PLAN.md §5.2). One per row;
         the guide spotlights the first viewport-visible one. */
      triggerProps={{ 'data-help-id': 'residents.row-actions' }}
    >
      {(close) => (
        <>
              <Link
                to={`/community/residents/${resident.resident_id}`}
                className="rp-row-actions__item"
                style={{ textDecoration: 'none' }}
                onClick={close}
              >
                <svg className="rp-icon-md rp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                View Profile
              </Link>
              {/* Absent when the campus's EHR owns resident records (the page
                  withholds the handler). Same guard style as Relocate below. */}
              {active && typeof onEdit === 'function' ? (
                /* Blocked items say why in a popover and ignore the click, so the page's
                   guard does not answer with a toast as well (BlockableMenuItem). */
                <BlockableMenuItem
                  reason={editLockedReason}
                  onClick={() => { onEdit(resident); close(); }}
                  className="rp-row-actions__item"
                >
                  <svg className="rp-icon-md rp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                  </svg>
                  Edit Resident
                </BlockableMenuItem>
              ) : null}
              {active ? (
                <button
                  type="button"
                  onClick={() => { onManageDevices(resident); close(); }}
                  data-help-id="row-actions.manage-devices"
                  className="rp-row-actions__item"
                >
                  <i className={"fe fe-tablet text-gray-600"}></i>
                  Manage Devices
                </button>
              ) : null}
              {/* Relocate — active residents only; a moved-out resident has no
                  room to move out of. */}
              {!locked && active && typeof onRelocate === 'function' ? (
                <BlockableMenuItem
                  reason={alarmBlockedReason}
                  onClick={() => { onRelocate(resident); close(); }}
                  className="rp-row-actions__item"
                >
                  <RelocateIcon />
                  Relocate
                </BlockableMenuItem>
              ) : null}
              {!locked && active && typeof onMoveOut === 'function' ? (
                <BlockableMenuItem
                  reason={alarmBlockedReason}
                  onClick={() => { onMoveOut(resident); close(); }}
                  className="rp-row-actions__item rp-row-actions__item--danger"
                  /* Help-guide anchor. Only exists while the menu is open, which is
                     what makes it a usable completion signal for the step before. */
                  data-help-id="row-actions.move-out"
                >
                  <svg className="rp-icon-md rp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                  </svg>
                  Move Out
                </BlockableMenuItem>
              ) : null}
        </>
      )}
    </RowActionsMenu>
  );
};

export default ResidentTable;
